import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import {
  fetchIntelligenceProductStatus,
  IntelligenceProductStatus,
} from '../api/ai';

type IntelligenceStatusState = {
  organizationId: string | null;
  status: IntelligenceProductStatus | null;
};

export const intelligenceProductStatusAtom = atom<IntelligenceStatusState>({
  organizationId: null,
  status: null,
});

export const creditsRemainingOf = (
  status: IntelligenceProductStatus | null | undefined
): number | null => {
  if (!status) return null;
  if (!status.capacityId) return 0;
  const derived = Math.max(
    0,
    (Number(status.creditsAllowance) || 0) +
      (Number(status.bonusCredits) || 0) -
      (Number(status.creditsUsed) || 0)
  );
  if (typeof status.creditsRemaining === 'number') {
    return Math.max(status.creditsRemaining, derived);
  }
  return derived;
};

export const useIntelligenceProductStatus = (
  organizationId: string | null | undefined
) => {
  const [state, setState] = useAtom(intelligenceProductStatusAtom);
  const status = useMemo(
    () =>
      organizationId && state.organizationId === organizationId
        ? state.status
        : null,
    [organizationId, state.organizationId, state.status]
  );

  const reload = useCallback(async () => {
    if (!organizationId) {
      setState({ organizationId: null, status: null });
      return null;
    }
    try {
      const next = await fetchIntelligenceProductStatus(organizationId);
      setState({ organizationId, status: next });
      return next;
    } catch (e) {
      console.error('Intelligence product-status failed', e);
      return null;
    }
  }, [organizationId, setState]);

  useEffect(() => {
    if (!organizationId) {
      setState({ organizationId: null, status: null });
      return;
    }
    void reload();
  }, [organizationId, reload, setState]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && organizationId) {
        void reload();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [organizationId, reload]);

  const applyCredits = useCallback(
    (creditsRemaining?: number | null, charged?: number) => {
      if (typeof creditsRemaining !== 'number' || !organizationId) return;
      setState(prev => {
        if (prev.organizationId !== organizationId || !prev.status) return prev;
        const pool =
          (Number(prev.status.creditsAllowance) || 0) +
          (Number(prev.status.bonusCredits) || 0);
        return {
          organizationId,
          status: {
            ...prev.status,
            creditsRemaining,
            creditsUsed:
              typeof charged === 'number'
                ? Math.max(0, pool - creditsRemaining)
                : prev.status.creditsUsed,
          },
        };
      });
    },
    [organizationId, setState]
  );

  return { status, reload, applyCredits };
};
